import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { Plus, LocateFixed, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const STAGES = ['New', 'Offer Sent', 'Follow-Up', 'Price Watch', 'Rejected Countered', 'Rejected NC', 'Accepted Other Offer', 'Under Contract', 'Marketed'];
const EMPTY = { street: '', city: '', state: 'FL', zip: '', county: '', price: '', status: 'New', note: '' };

const getPosition = () => new Promise((res, rej) => {
  if (!navigator.geolocation) return rej(new Error('unsupported'));
  navigator.geolocation.getCurrentPosition(p => res(p.coords), rej, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
});

export function QuickAddLead() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [key, setKey] = useState(0);
  const up = (k, v) => setF(x => ({ ...x, [k]: v }));

  const locate = useCallback(async () => {
    setLocating(true);
    try {
      const c = await getPosition();
      const r = await API.get('/geo/reverse', { params: { lat: c.latitude, lng: c.longitude } });
      setF(x => ({ ...x, street: r.data.street, city: r.data.city, state: r.data.state || 'FL', zip: r.data.zip, county: r.data.county }));
      setKey(k => k + 1);
      if (!r.data.has_number) toast.info('Found the street — add the house number');
    } catch (e) {
      toast.error(e?.code === 1 ? 'Location is blocked — allow it in your browser settings, or type the address' : e.response?.data?.detail || "Couldn't get your location — type the address");
    }
    setLocating(false);
  }, []);

  useEffect(() => { if (open) { setF(EMPTY); setKey(k => k + 1); locate(); } }, [open, locate]);

  const save = async () => {
    if (!f.street.trim()) return toast.error('Add the property address');
    setSaving(true);
    try {
      const r = await API.post('/leads', { name: f.street.trim(), property_address: f.street.trim(), property_city: f.city, property_state: f.state, property_zip: f.zip,
        county: f.county, asking_price: Number(String(f.price).replace(/[^\d.]/g, '')) || 0, status: f.status });
      if (f.note.trim()) await API.post(`/leads/${r.data.id}/activities`, { activity_type: 'note', description: f.note.trim() }).catch(() => {});
      setOpen(false);
      toast.success('Lead saved', { action: { label: 'Open', onClick: () => navigate(`/leads/${r.data.id}`) } });
      window.dispatchEvent(new Event('leads:changed'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save lead'); }
    setSaving(false);
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="quick-add-lead-fab" aria-label="Quick add lead"
        className="md:hidden fixed right-4 bottom-[calc(4.75rem+env(safe-area-inset-bottom))] z-30 grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] text-white shadow-[0_8px_28px_rgba(99,102,241,0.55)] transition-transform active:scale-95">
        <Plus className="h-7 w-7" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="quick-add-dialog">
          <DialogHeader><DialogTitle>Quick Add Lead</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Button variant="outline" onClick={locate} disabled={locating} className="w-full h-11" data-testid="quick-add-locate-btn">
              {locating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LocateFixed className="h-4 w-4 mr-2 text-[#0073ea]" />}
              {locating ? 'Finding the house you are at…' : 'Use my current location'}
            </Button>
            <AddressAutocomplete key={key} value={f.street} onChange={v => up('street', v)} placeholder="Property address" className="h-11"
              onPlaceSelect={p => setF(x => ({ ...x, street: p.street, city: p.city, state: p.state || 'FL', zip: p.zip, county: p.county }))} data-testid="quick-add-address" />
            <div className="grid grid-cols-[1fr_4rem_5.5rem] gap-2">
              <Input value={f.city} onChange={e => up('city', e.target.value)} placeholder="City" data-testid="quick-add-city" />
              <Input value={f.state} onChange={e => up('state', e.target.value.toUpperCase().slice(0, 2))} placeholder="ST" data-testid="quick-add-state" />
              <Input value={f.zip} onChange={e => up('zip', e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="ZIP" inputMode="numeric" data-testid="quick-add-zip" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input value={f.price} onChange={e => up('price', e.target.value)} placeholder="Asking price" inputMode="numeric" data-testid="quick-add-price" />
              <Select value={f.status} onValueChange={v => up('status', v)}>
                <SelectTrigger data-testid="quick-add-stage"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea rows={3} value={f.note} onChange={e => up('note', e.target.value)} placeholder="Note (optional) — e.g. vacant, roof damage, owner outside" data-testid="quick-add-note" />
            <Button onClick={save} disabled={saving} className="w-full h-11 bg-[#0073ea] hover:bg-[#0060c2] text-white" data-testid="quick-add-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Lead'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
